/**
 * OpraveneZavady — the repaired faults of a fleet car (P4-h): they left the damage map but stay on the car, newest
 * repair first, with who found / repaired them and what was done. At `write` a repaired fault can be reopened, which puts
 * it back on the map as it was — only while its part has no other active fault (the API refuses otherwise; the button
 * is disabled in that case so the user is told why before the click).
 */

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { formatDate } from '@/lib/format';
import type { Zavada, ZavadaOpravena } from './api';
import { ZAVAZNOST_TEXT } from './ExteriorMap';

interface OpraveneZavadyProps {
  opravene: ZavadaOpravena[];
  /** All the car's repaired faults; more than `opravene` holds when the list is cut. */
  celkem: number;
  /** The active faults: a part that has one cannot take a reopened fault. */
  active: Zavada[];
  canWrite: boolean;
  busy: boolean;
  onReopen: (id: number) => void;
}

const TONE = { nizka: 'neutral', stredni: 'warn', vysoka: 'danger' } as const;

export function OpraveneZavady({ opravene, celkem, active, canWrite, busy, onReopen }: OpraveneZavadyProps) {
  if (celkem === 0) {
    return (
      <p data-testid="opravene-prazdne" className="text-[13px] text-ink-2">
        Žádná závada zatím nebyla označena jako opravená.
      </p>
    );
  }
  const taken = new Set(active.map((z) => z.id_dilu));
  return (
    <div data-testid="opravene-seznam" className="flex flex-col">
      <div className="mb-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
        Opravené závady · {celkem > opravene.length ? `${opravene.length} z ${celkem}` : celkem}
      </div>
      <ul className="border border-line">
        {opravene.map((o) => (
          <li key={o.id} className="flex flex-wrap items-start gap-3 border-b border-line px-3 py-2 last:border-b-0">
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] text-ink">
                {o.nazev_dilu}
                {o.zona === 'interior' && <span className="text-ink-3"> · interiér</span>}
              </span>
              <span className="block text-[12px] text-ink-2 [overflow-wrap:anywhere]">{o.popis}</span>
              <span className="block text-[12px] text-ink-3 [overflow-wrap:anywhere]">
                Nalezeno {formatDate(o.nalezeno_at)}
                {o.nalezl ? ` · ${o.nalezl}` : ''}
                {o.id_rezervace_nalezeno != null ? ' · při vrácení rezervace' : ''}
              </span>
              <span className="block text-[12px] text-ink-3 [overflow-wrap:anywhere]">
                Opraveno {formatDate(o.opraveno_at)}
                {o.opravil ? ` · ${o.opravil}` : ''}
                {o.id_rezervace_opraveno != null ? ' · při vrácení rezervace' : ''}
                {o.poznamka_oprava ? ` · ${o.poznamka_oprava}` : ''}
              </span>
            </span>
            <Badge tone={TONE[o.zavaznost]} size="sm">
              {ZAVAZNOST_TEXT[o.zavaznost]}
            </Badge>
            {canWrite && (
              <Button
                variant="secondary"
                size="sm"
                disabled={busy || taken.has(o.id_dilu)}
                title={taken.has(o.id_dilu) ? 'Tento díl má jinou aktivní závadu.' : undefined}
                onClick={() => onReopen(o.id)}
              >
                Znovu otevřít
              </Button>
            )}
          </li>
        ))}
      </ul>
      {celkem > opravene.length && (
        <p className="mt-2 text-[12px] text-ink-3">Zobrazeno nejnovějších {opravene.length}, starší opravy jsou v historii změn.</p>
      )}
    </div>
  );
}
